"use client";

import * as React from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";

import "@/styles/global.css";

import type { Metadata } from "@/types/metadata";
import { appConfig } from "@/config/app";
import { getSettings as getPersistedSettings } from "@/lib/settings";
import { ThemeProvider } from "@/components/core//theme-provider";
import { Analytics } from "@/components/core/analytics";
import { AuthProvider } from "@/components/core/auth-provider";
import { I18nProvider } from "@/components/core/i18n-provider";
import { LocalizationProvider } from "@/components/core/localization-provider";
import { Rtl } from "@/components/core/rtl";
import { SettingsProvider } from "@/components/core/settings/settings-context";
import { Toaster } from "@/components/core/toaster";
import { AnimationProvider } from "@/components/core/animation-provider";
import { queryClient } from "@/lib/query-client";
import { QueryClientProvider } from "@tanstack/react-query";

const metadata = { title: appConfig.name } satisfies Metadata;

export interface RootProps {
	children: React.ReactNode;
}

export function Root({ children }: RootProps): React.JSX.Element {
	const settings = getPersistedSettings();

	return (
		<HelmetProvider>
			<Helmet>
				<title>{metadata.title}</title>
				<meta content={appConfig.themeColor} name="theme-color" />
			</Helmet>
			<>
				<Analytics>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <AnimationProvider>
                <LocalizationProvider>
                  <SettingsProvider settings={settings}>
                    <I18nProvider>
                      <Rtl>
                        <ThemeProvider>
                          {children}
                          <Toaster
                            position="top-right"
                            /*
                             * Kept top-right rather than moved to the bottom, and pushed clear of the
                             * fixed header.
                             *
                             * Top-right at sonner's default 24px offset lands the toast squarely on
                             * the "Currently impersonating …" label and the avatar — the two bits of
                             * chrome that say who you are acting as — covered by a toast that fires on
                             * every autosave. Bottom-right looks like the obvious answer and is worse:
                             * the wizard's Back / Save & exit / Continue row is in flow at the bottom
                             * of the content column, so when the user scrolls to it and clicks
                             * Continue — which is exactly what fires the save toast — the toast lands
                             * on the buttons they are still using and can swallow the next click.
                             *
                             * So: same corner, below the chrome. `--MainNav-height` is set on `body`
                             * by whichever layout is mounted (56px dashboard, 72px marketing) and
                             * inherits down to here; the fallback covers the auth screens, which set
                             * neither.
                             */
                            offset={{ top: "calc(var(--MainNav-height, 56px) + 16px)", right: "24px" }}
                            mobileOffset={{
                              top: "calc(var(--MainNav-height, 56px) + 12px)",
                              right: "12px",
                              left: "12px",
                            }}
                            /*
                             * The wizard autosaves on every step, so these arrive in bursts. Three at
                             * a time, gone in three seconds, confirms the save without building a wall
                             * down the side of the page.
                             */
                            visibleToasts={3}
                            duration={3000}
                            gap={8}
                            /*
                             * sonner ships a light-mode palette of its own. Pointing its three surface
                             * variables at the MUI tokens makes a toast the same kind of object as a
                             * tooltip and a picker popover — solid ground, divider hairline, real
                             * shadow — and keeps it legible in whichever scheme the app is in rather
                             * than hard-coding dark.
                             */
                            style={
                              {
                                "--normal-bg": "var(--mui-palette-background-level1)",
                                "--normal-text": "var(--mui-palette-text-primary)",
                                "--normal-border": "var(--mui-palette-divider)",
                              } as React.CSSProperties
                            }
                            toastOptions={{
                              style: {
                                borderRadius: "12px",
                                boxShadow: "var(--mui-shadows-16, 0px 6px 32px rgba(0, 0, 0, 0.5))",
                              },
                            }}
                          />
                        </ThemeProvider>
                      </Rtl>
                    </I18nProvider>
                  </SettingsProvider>
                </LocalizationProvider>
              </AnimationProvider>
            </AuthProvider>
          </QueryClientProvider>
				</Analytics>
			</>
		</HelmetProvider>
	);
}
